import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, from, map, switchMap } from 'rxjs';
import { saveAs } from 'file-saver';

export type ReportFormat = 'pdf' | 'excel' | 'word';

export const DEFAULT_REPORT_FORMAT: ReportFormat = 'pdf';

export interface QueryParamDTO {
  editionCode?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class ReportService {
  protected resourceUrl = `${environment.SERVER_READ_API_URL}/api/read`;

  protected reportsUrl = `${environment.SERVER_READ_API_URL}/api/read`;

  private readonly isDocx = signal<boolean>(false);

  constructor(protected http: HttpClient) {}

  /**
   * Generate report with dynamic X-Is-Docx header (single endpoint)
   */
  generateReport(queryName: string, queryParam: QueryParamDTO): Observable<Blob> {
    // Single endpoint URL - no format in path
    const url = `${this.reportsUrl}/generate/${queryName}`;

    // Set headers dynamically based on isDocx signal
    const headers = new HttpHeaders({
      'X-Is-Docx': this.isDocx().toString(),
    });

    return this.http
      .post(url, queryParam, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          // Handle the file download
          const blob = response.body;

          const fileName = response.headers.get('Filename');
          saveAs(blob, fileName);

          return blob;
        })
      );
  }

  /**
   * Set whether to generate DOCX format
   */
  setIsDocx(isDocx: boolean): void {
    this.isDocx.set(isDocx);
  }

  /**
   * Get current isDocx state
   */
  getIsDocx(): boolean {
    return this.isDocx();
  }

  /**
   * Generate Excel report
   */
  generateExcelReport(queryName: string, queryParam: QueryParamDTO): Observable<Blob> {
    const url = `${this.reportsUrl}/excel/${queryName}`;

    return this.http
      .post(url, queryParam, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          // Handle the file download
          const blob = response.body;

          const fileName = response.headers.get('Filename');
          saveAs(blob, fileName);
          return blob;
        })
      );
  }

  report(
    queryName: string,
    _env: string,
    queryParam: QueryParamDTO,
    format: ReportFormat = DEFAULT_REPORT_FORMAT,
    _pageHeight?: number,
    _pageWidth?: number,
    _whereParam?: boolean
  ): Observable<{ reportFile: string; reportFormat: ReportFormat }> {
    const headers = new HttpHeaders({
      'X-Is-Docx': (format === 'word').toString(),
    });

    return this.http
      .post(`${this.reportsUrl}/generate/${queryName}`, queryParam, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        switchMap((response) => from(response.body.arrayBuffer())),
        map((buffer) => {
          const bytes = new Uint8Array(buffer);
          let binary = '';

          bytes.forEach((byte) => {
            binary += String.fromCodePoint(byte);
          });

          return {
            reportFile: btoa(binary),
            reportFormat: format,
          };
        })
      );
  }
}
